import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, TableSortable, Typography } from '@saviynt/design-system';

import useFormApiEnabled from '../../../../hooks/use-form-api-enabled';
import { toTitleCase } from '../../../../utils';
import { UserSkeleton } from '../../../Common';
import { type User } from '../../types';
import { INITIAL_USER_LIST_COLUMNS } from '../constants';

import UserActionMenu from './UserActionMenu/UserActionMenu';

import './UsersSortableList.css';

interface UsersSortableListProps {
  allUsers: User[];
  skeletonCount: number;
  hasMore: boolean;
  isLoadingRequestableUsers: boolean;
  isFetchingRequestableUsers: boolean;
  setLastElement: (element: HTMLDivElement | null) => void;
  offset: number;
}

function UsersSortableList({
  allUsers,
  skeletonCount,
  hasMore,
  isLoadingRequestableUsers,
  isFetchingRequestableUsers,
  setLastElement,
  offset,
}: UsersSortableListProps) {
  const { isFormApiEnabled } = useFormApiEnabled();

  const initialColumns = useMemo(
    () =>
      isFormApiEnabled
        ? INITIAL_USER_LIST_COLUMNS
        : INITIAL_USER_LIST_COLUMNS.filter((col) => col.key !== 'actionColumn'),
    [isFormApiEnabled]
  );

  const [columns, setColumns] = useState(INITIAL_USER_LIST_COLUMNS);

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  const skeletonRows = Array.from(
    { length: skeletonCount },
    (_, idx) => `skeleton-${offset}-${idx}`
  ).map((key) => ({
    username: <UserSkeleton key={key} />,
    userSource: <UserSkeleton />,
    employeeType: <UserSkeleton />,
    email: <UserSkeleton />,
    manager: <UserSkeleton />,
    statuskey: <UserSkeleton />,
    actionColumn: <UserSkeleton />,
  }));

  const triggerRow =
    hasMore && !isLoadingRequestableUsers && !isFetchingRequestableUsers
      ? [
          {
            username: (
              <div
                ref={setLastElement}
                className='UsersSortableList-triggerRow'
              />
            ),
            userSource: '',
            employeeType: '',
            email: '',
            manager: '',
            statuskey: '',
            actionColumn: '',
          },
        ]
      : [];

  const rows = allUsers.map((user) => ({
    username: (
      <Box className='UsersSortableList-usernameCell'>
        <Box className='UsersSortableList-avatarWrapper'>
          <Avatar size='small' />
        </Box>
        <Box className='UsersSortableList-userInfo'>
          <Typography
            kind='body2-bold'
            color='--color-foreground-secondary-bold'
            lineLimit={1}>
            {user.firstname} {user.lastname}
          </Typography>
          <Typography
            kind='body2'
            color='--color-foreground-neutral-subtle'
            lineLimit={1}>
            {user.username}
          </Typography>
        </Box>
      </Box>
    ),
    userSource: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.userSource ?? '-'}
      </Typography>
    ),
    employeeType: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */}
        {user.employeeType ? toTitleCase(user.employeeType) : '-'}
      </Typography>
    ),
    email: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.email ?? '-'}
      </Typography>
    ),
    manager: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.manager ?? '-'}
      </Typography>
    ),
    statuskey: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.statuskey === 1 ? 'Active' : 'Inactive'}
      </Typography>
    ),
    actionColumn: isFormApiEnabled ? (
      <UserActionMenu userkey={user.userkey} />
    ) : null,
  }));

  return (
    <TableSortable
      data={[...rows, ...skeletonRows, ...triggerRow]}
      columns={columns}
      setColumns={setColumns}
      className='UsersSortableList-TableSortable'
      isWindowScroll={false}
      tableHeight='var(--users-list-table-height)'
    />
  );
}

export default UsersSortableList;
